import { KeyboardEvent as ReactKeyboardEvent, MouseEvent } from "react";

import { css } from "@compiled/react";

import { ProductKeys } from "@atlassiansox/asc-stargate";

import { getTemplateType } from "@/components/confluence/templates/util/template-util";
import { useSiteSelectorContext } from "@/components/site-selector-modal";
import { Template, TemplateType } from "@/content/templates/template.types.ts";

import { TemplateDescriptionSection } from "../sections/template-description-section";
import { TemplatePreviewSection } from "../sections/template-preview-section";

import { TemplateCtaButtons } from "./template-cta-buttons.tsx";
import { TemplateRelatedTemplatesSection } from "./template-related-templates-section.tsx";
import { TemplateTabbedSection } from "./template-tabbed-section.tsx";

interface TemplateDetailsBodySectionProps {
    template: Template;
}

const templateDetailsBodyStyles = css({
    "@media(max-width: 768px)": {
        height: "auto"
    }
});

const ctaButtonContainerStyles = css({
    "display": "none",
    "@media(max-width: 768px)": {
        display: "block",
        marginBlockStart: "64px"
    }
});

const previewSectionStyles = css({
    cursor: "pointer"
});

export const TemplateDetailsBody = ({
    template
}: TemplateDetailsBodySectionProps) => {
    const { openModalForTemplate } = useSiteSelectorContext();

    const buttonUseTemplateClicked = () => {
        openModalForTemplate({
            templateId: template.pluginModuleKey ?? "",
            templateName: template.name ?? "",
            productKey: ProductKeys.CONFLUENCE
        });
    };

    const navigateToSignupClick = (event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        event.preventDefault();
        buttonUseTemplateClicked();
    };

    const navigateToSignupKeyDown = (event: ReactKeyboardEvent) => {
        if (event.code === "Space" || event.code === "Enter") {
            event.stopPropagation();
            event.preventDefault();
            buttonUseTemplateClicked();
        }
    };

    const isWhiteBoardTemplate =
        getTemplateType(template).type === TemplateType.Whiteboard;

    return (
        <div css={templateDetailsBodyStyles} data-testid="TemplateDetailsBody">
            {}
            <div
                onClick={
                    isWhiteBoardTemplate ? undefined : navigateToSignupClick
                }
                onKeyDown={
                    isWhiteBoardTemplate ? undefined : navigateToSignupKeyDown
                }
                css={!isWhiteBoardTemplate && previewSectionStyles}
                role="img"
                aria-label="Template preview"
            >
                <TemplatePreviewSection
                    template={template}
                    hideBackground={true}
                />
            </div>
            <TemplateDescriptionSection template={template} />
            <TemplateTabbedSection template={template} />

            <div css={ctaButtonContainerStyles}>
                <TemplateCtaButtons
                    name={template.name || ""}
                    pluginModuleKey={template.pluginModuleKey || ""}
                />
            </div>
            <TemplateRelatedTemplatesSection template={template} />
        </div>
    );
};
